/* Shopify-priced variants, serialized changes, and a durable journal for interrupted swaps. */
(() => {
  'use strict';
  const configNode = document.getElementById('souvenir-pint-commerce-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent), pricing = window.SouvenirPintCartModel;
  const journalKey = `souvenir-pint-change:${config.productId}`;
  let catalogPromise;
  const money = cents => new Intl.NumberFormat('en-US', {style:'currency',currency:config.currency || 'USD'}).format(cents / 100);
  async function request(path, body) {
    const response = await fetch(`${config.root}${path}`, {method:body ? 'POST' : 'GET',credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json',...(body ? {'Content-Type':'application/json'} : {})},...(body ? {body:JSON.stringify(body)} : {})});
    const result = await response.json();
    if (!response.ok) { const error = Error(result.description || 'Your cart could not be updated.'); error.confirmedFailure = response.status >= 400 && response.status < 500; throw error; }
    return result;
  }
  function catalog() {
    if (!catalogPromise) catalogPromise = fetch(config.catalogUrl).then(r => {if (!r.ok) throw Error('Designs could not load.'); return r.json();}).then(d => {if (!Array.isArray(d.designs)) throw Error('Designs could not load.'); return d.designs;}).catch(e => {catalogPromise = null; throw e;});
    return catalogPromise;
  }
  function save(journal) { localStorage.setItem(journalKey, JSON.stringify(journal)); }
  function tokenLines(cart, token) { return cart.items.filter(item => item.properties?._pint_transaction === token); }
  // Shopify changes line keys when discounts change. Persist the complete stable
  // identity, then use only freshly-read keys for removal.
  function identity(item) {
    return JSON.stringify([String(item.variant_id ?? item.id),
      Object.entries(item.properties || {}).sort(([a],[b]) => a.localeCompare(b)),
      String(item.selling_plan_allocation?.selling_plan?.id || '')]);
  }
  function sameAdded(actual, expected) {
    return actual.length === expected.length && expected.every(want => {
      const matches = actual.filter(item => identity(item) === identity(want));
      return matches.length === 1 && matches[0].quantity === want.quantity;
    });
  }
  function snapshot(item) { return {key:item.key,quantity:item.quantity,identity:identity(item)}; }
  function originalLines(cart, journal) {
    const available = cart.items.filter(item => item.properties?._pint_transaction !== journal.token);
    const originals = [];
    for (const old of journal.old) {
      const matches = available.filter(item => old.identity ? identity(item) === old.identity : item.key === old.key);
      if (matches.length > 1 || (matches.length && (matches[0].quantity !== old.quantity || originals.includes(matches[0])))) throw Error('Your cart changed in another window. Please contact us to review the glass selection before checkout.');
      if (matches.length) originals.push(matches[0]);
    }
    return originals;
  }
  async function rollback(journal, cart) {
    // Old key-only journals cannot identify a discount-rekeyed original safely.
    // Undo only this verified addition; never guess which old lines to delete.
    const added = tokenLines(cart, journal.token);
    const matches = added.every(item => journal.items.filter(want => identity(item) === identity(want) && item.quantity === want.quantity).length === 1);
    if (!matches || (added.length && journal.phase !== 'rollback' && (!sameAdded(added,journal.items) || added.reduce((sum,item) => sum + item.original_price * item.quantity,0) !== journal.expected))) throw Error('Please contact us to review the unfinished glass update before checkout.');
    journal.phase = 'rollback'; save(journal);
    if (added.length) {
      try { await request('cart/update.js',{updates:Object.fromEntries(added.map(item => [item.key,0]))}); }
      catch (_) { /* Check before retrying an idempotent removal. */ }
    }
    cart = await request('cart.js');
    if (tokenLines(cart,journal.token).length) throw Error('Your glass update is not finished. Choose “Check cart again” before continuing.');
    localStorage.removeItem(journalKey);
    return cart;
  }
  async function finish(journal) {
    let cart = await request('cart.js');
    if (!cart.items.length) {
      localStorage.removeItem(journalKey);
      if (journal.items.length && journal.phase !== 'rollback') throw Error('The last glass change was not applied. Please check your selection and try again.');
      return cart;
    }
    if (journal.phase === 'rollback') return rollback(journal,cart);
    const added = tokenLines(cart, journal.token);
    if (journal.phase === 'adding' && journal.items.length && !added.length && originalLines(cart,journal).length === journal.old.length) {
      localStorage.removeItem(journalKey);
      throw Error('The last glass change was not applied. Please check your selection and try again.');
    }
    if (!sameAdded(added, journal.items)) throw Error('We couldn’t confirm the last glass update. Use “Check cart again” before continuing; do not add the items again.');
    if (added.reduce((sum, item) => sum + item.original_price * item.quantity, 0) !== journal.expected) throw Error('The saved glass price changed. Please contact us before checking out.');
    const originals = originalLines(cart, journal);
    if (journal.old.some(old => !old.identity)) {
      if (originals.length !== journal.old.length) {
        if (journal.phase === 'adding') return rollback(journal,cart);
        throw Error('Please contact us to review the unfinished glass update before checkout.');
      }
      journal.old = originals.map(snapshot); save(journal);
    }
    if (journal.phase === 'adding' && originals.length !== journal.old.length) throw Error('Your cart changed in another window. Please contact us before checkout.');
    journal.phase = 'removing'; save(journal);
    if (originals.length) {
      try { await request('cart/update.js', {updates:Object.fromEntries(originals.map(item => [item.key,0]))}); }
      catch (_) { /* A removal is idempotent. Read first; never repeat an add. */ }
    }
    cart = await request('cart.js');
    const finalAdded = tokenLines(cart,journal.token);
    if (finalAdded.reduce((sum,item) => sum + item.original_price * item.quantity,0) !== journal.expected) throw Error('The saved glass price changed. Please contact us before checking out.');
    if (originalLines(cart, journal).length || !sameAdded(finalAdded,journal.items)) throw Error('Your glass update is not finished. Choose “Check cart again” before continuing.');
    localStorage.removeItem(journalKey);
    return cart;
  }
  async function recover() {
    const saved = localStorage.getItem(journalKey);
    return saved ? finish(JSON.parse(saved)) : request('cart.js');
  }
  async function replace(cart, ids, designs) {
    const old = pricing.readCart(cart,designs,config.productId);
    if (old.invalid.length) throw Error('A glass pack has incomplete design details. Remove that pack before continuing.');
    const token = crypto.randomUUID();
    const planned = pricing.plan(ids,designs,config.variants,token);
    const journal = {token,old:old.lines.map(snapshot),items:planned.items,expected:planned.expected,phase:'adding'};
    save(journal); // No cart change begins unless recovery data can be persisted.
    if (planned.items.length) {
      try { await request('cart/add.js',{items:planned.items}); }
      catch (error) {
        if (error.confirmedFailure) {
          const checked = await request('cart.js');
          if (!tokenLines(checked,token).length) { localStorage.removeItem(journalKey); throw error; }
        }
        // An uncertain response may already have applied. Finish reads the unique token.
      }
    }
    return finish(journal);
  }
  async function locked(work) {
    if (!navigator.locks) throw Error('Glass shopping is unavailable in this browser right now. Please try a current browser or contact us.');
    return navigator.locks.request('souvenir-cart', async () => {
      const designs = await catalog(), cart = await recover();
      const result = await work(cart,designs);
      window.dispatchEvent(new CustomEvent('souvenir:cart-updated',{detail:result}));
      return result;
    });
  }
  const api = {
    config, money, total:pricing.total, catalog,
    inspect: (cart,designs) => pricing.readCart(cart,designs,config.productId),
    get: () => locked(async (cart,designs) => {
      const found = pricing.readCart(cart,designs,config.productId);
      if (found.invalid.length) return cart;
      const currentPrice = found.lines.reduce((sum,item) => sum + item.original_price * item.quantity,0);
      return found.lines.length && (currentPrice !== pricing.total(found.glasses.length,found.double) || found.lines.some(item => item.properties?._pint_version !== '1')) ? replace(cart,found.glasses,designs) : cart;
    }),
    add: ids => locked((cart,designs) => {
      const found = pricing.readCart(cart,designs,config.productId);
      return replace(cart,[...found.glasses,...ids],designs);
    }),
    quantity: (id,sides,quantity) => locked((cart,designs) => {
      if (!Number.isInteger(quantity) || quantity < 0 || quantity > 10000 || ![1,2].includes(sides)) throw Error('Choose a valid quantity.');
      const found = pricing.readCart(cart,designs,config.productId);
      return replace(cart,[...found.glasses.filter(s => s.id !== id || s.sides !== sides),...Array.from({length:quantity},()=>({id,sides}))],designs);
    }),
    removeLine: key => locked(async cart => {
      if (!cart.items.some(item => item.key === key)) return cart;
      await request('cart/change.js',{id:key,quantity:0});
      return request('cart.js');
    }),
    checkout: async () => {
      const cart = await api.get();
      const found = pricing.readCart(cart,await catalog(),config.productId);
      if (found.invalid.length) throw Error('Remove the incomplete glass pack before checkout.');
      if (!cart.item_count) throw Error('Your cart is empty.');
      if (!window.SouvenirCheckout) throw Error('Checkout is loading. Please try again.');
      return window.SouvenirCheckout();
    }
  };
  window.SouvenirPints = api;
  window.dispatchEvent(new Event('souvenir:commerce-ready'));
})();
